import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { parentOf, type Container } from "@egalregal/shared";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import type { OwnCatalogEntry } from "../../shared/api/requests";

function ancestorChain(containers: readonly Container[], containerId: string): string[] {
  const chain: string[] = [];
  let curId: string | undefined = containerId;
  while (curId && !chain.includes(curId)) {
    chain.push(curId);
    const parent = containers.find((c) => c.id === curId);
    curId = parent ? parentOf(containers, parent.id)?.id : undefined;
  }
  return chain;
}

/**
 * The caller's own entry that currently holds `containerId`: the entry whose
 * roots contain the container or one of its ancestors. A container inside a
 * shared entry matches no own root, so there is nothing the caller may pick.
 */
export function ownEntryForContainer(
  entries: readonly OwnCatalogEntry[],
  containers: readonly Container[],
  containerId: string,
): OwnCatalogEntry | undefined {
  const chain = ancestorChain(containers, containerId);
  return entries.find((entry) => entry.roots.some((root) => chain.includes(root)));
}

/**
 * Own-entry assignment picker for a container (catalog-entry-assignment 04).
 * Shows only for containers that belong to a catalog entry the caller owns;
 * a container living in a shared entry keeps its assignment (sharees cannot
 * move it). The edit form controls the value and applies the choice only after
 * the metadata save succeeds (container-view 04).
 */
export function ContainerEntrySelector({
  containerId,
  value,
  onChange,
}: {
  containerId: string;
  /** The entry to display; defaults to the derived owning entry. */
  value?: string;
  onChange: (entryId: string) => void;
}) {
  const t = useT();
  const containers = useAppStore((s) => s.containers);
  const entries = useAppStore((s) => s.entries);

  const assigned = ownEntryForContainer(entries, containers, containerId);
  if (!assigned) return null;

  return (
    <TextField
      select
      size="small"
      label={t("catalogAssignment")}
      value={value ?? assigned.id}
      data-testid="container-entry-selector"
      onChange={(event) => onChange(event.target.value)}
      sx={{ minWidth: 180 }}
    >
      {entries.map((entry) => (
        <MenuItem key={entry.id} value={entry.id}>
          {entry.name}
        </MenuItem>
      ))}
    </TextField>
  );
}
